// src/components/exam-builder/PassageMcqModal.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  sentence_fa: "",
  sentence_en: "",
  layout: "left-right",
  correctSlot: 1,
  markerText: "[]",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};

  const text_fa = body?.text?.fa || "";
  const text_en = body?.text?.en || "";
  const sentence_fa = body?.sentence?.fa || "";
  const sentence_en = body?.sentence?.en || "";

  const layout = body.layout || "left-right";
  const markerText = body.markerText || "[]";

  const scoring = body.scoring || {};
  const correctSlot =
    typeof scoring.correctSlot === "number" &&
    !Number.isNaN(scoring.correctSlot)
      ? scoring.correctSlot
      : 1;

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    sentence_fa,
    sentence_en,
    layout,
    correctSlot,
    markerText,
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function PassageMcqModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  React.useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // شمارش تعداد اسلات‌ها بر اساس markerText روی متن فعال (fa>en)
  const marker = useMemo(() => form.markerText || "[]", [form.markerText]);
  const activeTextForCounting = useMemo(
    () => (form.text_fa?.trim() ? form.text_fa : form.text_en || ""),
    [form.text_fa, form.text_en]
  );
  const slotCount = useMemo(() => {
    if (!activeTextForCounting || !marker) return 0;
    // چون متن HTML است، شمارش را روی HTML انجام می‌دهیم (برای سادگی).
    // اگر بعدها خواستی دقیق‌تر باشی، می‌توانی تگ‌ها را حذف کنی.
    const re = new RegExp(
      marker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // escape
      "g"
    );
    return (activeTextForCounting.match(re) || []).length;
  }, [activeTextForCounting, marker]);

  // به‌روز کردن correctSlot وقتی تعداد اسلات‌ها تغییر می‌کند
  React.useEffect(() => {
    if (slotCount === 0) return;
    setForm((f) => ({
      ...f,
      correctSlot: Math.min(Math.max(1, Number(f.correctSlot) || 1), slotCount),
    }));
  }, [slotCount]);

  if (!isOpen) return null;

  const validate = () => {
    if (!form.text_fa.trim() && !form.text_en.trim()) {
      toast.error(
        t(
          "passage_mcq.toasts.passage_required",
          "Passage is required (FA or EN)."
        )
      );
      return false;
    }
    if (!form.sentence_fa.trim() && !form.sentence_en.trim()) {
      toast.error(
        t(
          "passage_mcq.toasts.sentence_required",
          "Sentence is required (FA or EN)."
        )
      );
      return false;
    }
    if (slotCount < 1) {
      toast.error(
        t(
          "passage_mcq.toasts.no_slots",
          "No slots found. Put [] where the sentence can be inserted."
        )
      );
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      kind: "passage_mcq",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        text: {
          fa: form.text_fa || undefined,
          en: form.text_en || undefined,
        },
        sentence: {
          fa: form.sentence_fa || undefined,
          en: form.sentence_en || undefined,
        },
        layout: form.layout || "left-right",
        markerText: form.markerText || "[]",
        scoring: {
          mode: "exact",
          correctSlot: Number(form.correctSlot) || 1, // 1-based
        },
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? String(form.tags)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("passage_mcq.toasts.updated", "Updated successfully")
          : t("passage_mcq.toasts.created", "Created successfully")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error", "Unknown error"));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      {/* Passage Editors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.passage_fa", "Passage (FA)")}
            <span className="ml-2 text-xs text-gray-500">
              {t("passage_mcq.marker_hint", "Use [] to mark insert positions")}
            </span>
          </label>
          <CustomEditor
            value={form.text_fa}
            onEditorChange={(content) => setField("text_fa", content)}
            placeholder={t(
              "passage_mcq.passage_fa_ph",
              "Paste your FA passage here. Put [] where sentence can be inserted."
            )}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.passage_en", "Passage (EN)")}
            <span className="ml-2 text-xs text-gray-500">
              {t("passage_mcq.marker_hint", "Use [] to mark insert positions")}
            </span>
          </label>
          <CustomEditor
            value={form.text_en}
            onEditorChange={(content) => setField("text_en", content)}
            placeholder={t(
              "passage_mcq.passage_en_ph",
              "Paste your EN passage here. Put [] where sentence can be inserted."
            )}
          />
        </div>
      </div>

      {/* Sentence Editors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.sentence_fa", "جمله")}
          </label>
          <textarea
            className="w-full border p-3 rounded-xl"
            value={form.sentence_fa}
            onChange={(e) => setField("sentence_fa", e.target.value)}
            placeholder={t(
              "passage_mcq.sentence_fa_ph",
              "Write the sentence that will be inserted."
            )}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.sentence_en", "Sentence (EN)")}
          </label>
          <textarea
            className="w-full border p-3 rounded-xl"
            value={form.sentence_en}
            onChange={(e) => setField("sentence_en", e.target.value)}
            placeholder={t(
              "passage_mcq.sentence_en_ph",
              "Write the sentence that will be inserted."
            )}
          />
        </div>
      </div>

      {/* Stem (Instruction) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.stem_fa", "Instruction (FA)")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => setField("stem_fa", content)}
            placeholder={t(
              "passage_mcq.stem_fa_ph",
              "e.g. Click on the square where the sentence best fits."
            )}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.stem_en", "Instruction (EN)")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => setField("stem_en", content)}
            placeholder={t(
              "passage_mcq.stem_en_ph",
              "e.g. Click on the square where the sentence best fits."
            )}
          />
        </div>
      </div>

      {/* Layout & Correct Slot */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.layout", "Layout")}
          </label>
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {["left-right", "right-left", "top-bottom", "bottom-top"].map(
              (l) => (
                <label key={l} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="layout"
                    value={l}
                    checked={form.layout === l}
                    onChange={(e) => setField("layout", e.target.value)}
                  />
                  <span>{t(`passage_mcq.layout_${l}`, l)}</span>
                </label>
              )
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.correct_slot", "Correct position")}
          </label>
          <div className="flex items-center gap-3">
            <select
              className="border rounded-lg px-3 py-2"
              value={form.correctSlot}
              onChange={(e) => setField("correctSlot", Number(e.target.value))}
              disabled={slotCount < 1}
            >
              {Array.from(
                { length: Math.max(slotCount, 0) },
                (_, i) => i + 1
              ).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-600">
              {t("passage_mcq.slot_count", {
                defaultValue: "Slots: {{n}}",
                n: slotCount,
              })}
            </span>
          </div>
          {slotCount === 0 && (
            <div className="mt-1 text-xs text-amber-600">
              {t(
                "passage_mcq.add_marker_hint",
                "[] برای جای خالی این نشانه را وارد کنید"
              )}
            </div>
          )}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.points", "Points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setField("points", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.status", "Status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="published">
              {t("common.published", "Published")}
            </option>
            <option value="draft">{t("common.draft", "Draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("passage_mcq.tags", "Tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv", "comma,separated,tags")}
            value={form.tags}
            onChange={(e) => setField("tags", e.target.value)}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg border hover:bg-gray-50"
        >
          {t("common.cancel", "Cancel")}
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          {isEdit ? t("common.update", "Update") : t("common.create", "Create")}
        </button>
      </div>
    </form>
  );
}
